import { store } from './store';

const HEALTH_URL = `${import.meta.env.BASE_URL}api/health`;
const PROBE_TIMEOUT_MS = 4_000;
const SECOND_PROBE_DELAY_MS = 6_000;
let activeProbe: AbortController | null = null;
let latestProbe = 0;

/** Confirm real backend reachability instead of trusting a stale WebView online flag. */
export async function probeBackendConnectivity(): Promise<boolean> {
  const generation = ++latestProbe;
  activeProbe?.abort();
  const controller = new AbortController();
  activeProbe = controller;
  const timeout = window.setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const url = new URL(HEALTH_URL, location.href);
    url.searchParams.set('resumeCheck', String(Date.now()));
    await fetch(url, {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
    });
    // Any same-origin HTTP response proves network reachability. A 5xx is an
    // app/server problem, not an offline device, and must not disable every
    // feature guarded by store.online.
    if (generation === latestProbe) store.set({ online: true });
    return true;
  } catch {
    // A newer resume probe aborts the old one. Never let that old rejection
    // overwrite fresher state; only the latest timeout/network failure counts.
    if (generation !== latestProbe) return store.get().online;
    store.set({ online: false });
    return false;
  } finally {
    clearTimeout(timeout);
    if (activeProbe === controller) activeProbe = null;
  }
}

/**
 * Head units often report their first network state before Wi-Fi has settled.
 * Probe now and once more after the same short startup window used natively.
 */
export function installAndroidConnectivityRecovery(): void {
  let delayedProbe: number | null = null;
  let pairGeneration = 0;

  const checkTwice = (): void => {
    if (document.hidden) return;
    const generation = ++pairGeneration;
    if (delayedProbe !== null) clearTimeout(delayedProbe);
    void probeBackendConnectivity();
    delayedProbe = window.setTimeout(() => {
      delayedProbe = null;
      if (!document.hidden && generation === pairGeneration) void probeBackendConnectivity();
    }, SECOND_PROBE_DELAY_MS);
  };

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      checkTwice();
      return;
    }
    pairGeneration++;
    if (delayedProbe !== null) clearTimeout(delayedProbe);
    delayedProbe = null;
  });
  window.addEventListener('pageshow', checkTwice);
  checkTwice();
}
